import React, { useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const PriceComparisonChart = ({ data, marketName, loading }) => {
  const { peakHarvestPrices = [], storagePeriodPrices = [] } = data || {};

  const mspData = useMemo(() => [
    { year: 2015, MSP: 1325 },
    { year: 2016, MSP: 1365 },
    { year: 2017, MSP: 1425 },
    { year: 2018, MSP: 1700 },
    { year: 2019, MSP: 1760 },
    { year: 2020, MSP: 1850 },
    { year: 2021, MSP: 1870 },
    { year: 2022, MSP: 1962 },
    { year: 2023, MSP: 2090 },
    { year: 2024, MSP: 2225 }
  ], []);

  const tagMap = useMemo(() => {
    if (marketName !== 'Gulabbagh') return {};
    return {
      2016: 'Nov 2016 - Demonetisation (high storage demand)',
      2020: 'Mar 2020: covid1-low demand<br>Aug-Dec 2020: covid2 - demand picks up',
      2023: 'Dec 2023: ethanol surge'
    };
  }, [marketName]);

  const years = useMemo(() => mspData.map(item => item.year), [mspData]);

  const peakHarvestValues = useMemo(
    () => years.map(year => {
      const entry = peakHarvestPrices.find(item => item.year === year);
      return entry ? entry.Peak_Harvest_Prices : null;
    }),
    [years, peakHarvestPrices]
  );

  const storageAboveValues = useMemo(
    () => years.map((year, idx) => {
      const entry = storagePeriodPrices.find(item => item.year === year);
      const val = entry ? entry['Storage Period Prices'] : null;
      return val != null && peakHarvestValues[idx] != null && val > peakHarvestValues[idx]
        ? val
        : null;
    }),
    [years, storagePeriodPrices, peakHarvestValues]
  );

  const storageBelowValues = useMemo(
    () => years.map((year, idx) => {
      const entry = storagePeriodPrices.find(item => item.year === year);
      const val = entry ? entry['Storage Period Prices'] : null;
      return val != null && peakHarvestValues[idx] != null && val <= peakHarvestValues[idx]
        ? val
        : null;
    }),
    [years, storagePeriodPrices, peakHarvestValues]
  );

  const mspValues = useMemo(() => mspData.map(item => item.MSP), [mspData]);

  const plotBands = useMemo(() => {
    if (marketName !== 'Gulabbagh') return [];
    return years.reduce((bands, year, index) => {
      if (tagMap[year]) {
        bands.push({
          from: index - 0.4,
          to: index + 0.4,
          color: 'rgba(200,200,200,0.3)',
          label: {
            text: '',
            rotation: 0,
            style: { color: '#606060' }
          }
        });
      }
      return bands;
    }, []);
  }, [years, tagMap, marketName]);

  const options = useMemo(() => ({
    chart: { type: 'column' },
    title: { text: '' },
    xAxis: {
      categories: years,
      title: { text: 'Year' },
      plotBands: plotBands
    },
    yAxis: [
      { title: { text: 'Price (INR/Quintal)' } },
      { title: { text: 'MSP (INR)' }, opposite: true }
    ],
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const year = years[this.points[0].point.index];
        const tag = tagMap[year];
        let s = `<b>${year}</b><br/>`;
        this.points.forEach(point => {
          s += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y} INR</b><br/>`;
        });
        if (tag) {
          s += `<br/><span style="font-style: italic; color: #444">${tag}</span>`;
        }
        return s;
      }
    },
    legend: {
      align: 'center',
      verticalAlign: 'top',
      layout: 'horizontal',
      symbolRadius: 0,
      symbolHeight: 12,
      symbolWidth: 12
    },
    credits: { enabled: false },
    series: [
      {
        name: 'Peak Harvest Price',
        data: peakHarvestValues,
        type: 'column',
        color: 'rgba(134, 134, 130, 1)'
      },
      {
        name: 'Storage Price > Peak Harvest Price',
        data: storageAboveValues,
        type: 'column',
        color: 'rgba(91, 185, 140, 1)'
      },
      {
        name: 'Storage Price < Peak Harvest Price',
        data: storageBelowValues,
        type: 'column',
        color: 'rgba(247, 104, 8, 1)'
      },
      {
        name: 'MSP',
        data: mspValues,
        type: 'line',
        color: 'rgba(8, 128, 234, 1)',
        yAxis: 1
      }
    ]
  }), [
    years,
    plotBands,
    peakHarvestValues,
    storageAboveValues,
    storageBelowValues,
    mspValues,
    tagMap
  ]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default PriceComparisonChart;
